import React, { useEffect, useState } from 'react';
import api from '../api/client';
import { Championship } from '../types';
import './DiscordBot.css';

interface DiscordCategory {
  key: string;
  name: string;
}

interface ChampionshipDiscordConfig {
  championshipId: string;
  enabled: boolean;
  categoryChannels: Record<string, string>;
  adminChannelId: string;
  statisticsChannelId: string;
  adminRoleIds: string[];
  moderatorRoleIds: string[];
  userRoleIds: string[];
}

interface DiscordBotConfigData {
  enabled: boolean;
  botToken: string;
  applicationId: string;
  categories: DiscordCategory[];
  categoryChannels: Record<string, string>;
  messageRouting: Record<string, string>;
  adminChannelId: string;
  statisticsChannelId: string;
  adminRoleIds: string[];
  moderatorRoleIds: string[];
  userRoleIds: string[];
  championships: ChampionshipDiscordConfig[];
}

const MESSAGE_TYPES: Array<{ type: string; label: string }> = [
  { type: 'championship_created', label: 'Championship Announcement' },
  { type: 'race_result_imported', label: 'Race Results' },
  { type: 'rotation_applied', label: 'Next Round Info (fallback)' },
  { type: 'server_started', label: 'Practice Server Live' },
  { type: 'driver_registered', label: 'Driver Registered' },
  { type: 'driver_withdrew', label: 'Driver Withdrew' },
  { type: 'driver_signed_out', label: 'Driver Signed Out' },
  { type: 'driver_signed_in', label: 'Driver Signed In' },
  { type: 'new_personal_best', label: 'New Personal Best' },
  { type: 'new_overall_best', label: 'New Overall Best' },
  { type: 'penalty_added', label: 'Penalty Added' },
];

const DEFAULT_CATEGORIES: DiscordCategory[] = [{ key: 'public', name: 'Public' }];

const DEFAULT_ROUTING: Record<string, string> = Object.fromEntries(
  MESSAGE_TYPES.map((m) => [m.type, 'public'])
);

const EMPTY_CHAMP_CFG = (champId: string): ChampionshipDiscordConfig => ({
  championshipId: champId,
  enabled: false,
  categoryChannels: {},
  adminChannelId: '',
  statisticsChannelId: '',
  adminRoleIds: [],
  moderatorRoleIds: [],
  userRoleIds: [],
});

const EMPTY_CONFIG: DiscordBotConfigData = {
  enabled: false,
  botToken: '',
  applicationId: '',
  categories: DEFAULT_CATEGORIES,
  categoryChannels: {},
  messageRouting: DEFAULT_ROUTING,
  adminChannelId: '',
  statisticsChannelId: '',
  adminRoleIds: [],
  moderatorRoleIds: [],
  userRoleIds: [],
  championships: [],
};

const normaliseConfig = (raw: any): DiscordBotConfigData => {
  const categories: DiscordCategory[] = raw.categories?.length
    ? raw.categories
    : DEFAULT_CATEGORIES;

  // Migrate legacy publicChannelId → categoryChannels['public']
  const categoryChannels: Record<string, string> = { ...(raw.categoryChannels || {}) };
  if (!categoryChannels['public'] && raw.publicChannelId) {
    categoryChannels['public'] = raw.publicChannelId;
  }

  const messageRouting: Record<string, string> = {
    ...DEFAULT_ROUTING,
    ...(raw.messageRouting || {}),
  };

  const championships: ChampionshipDiscordConfig[] = (raw.championships || []).map((c: any) => {
    const catChannels: Record<string, string> = { ...(c.categoryChannels || {}) };
    if (!catChannels['public'] && c.publicChannelId) catChannels['public'] = c.publicChannelId;
    return {
      championshipId: c.championshipId,
      enabled: Boolean(c.enabled),
      categoryChannels: catChannels,
      adminChannelId: c.adminChannelId || '',
      statisticsChannelId: c.statisticsChannelId || '',
      adminRoleIds: c.adminRoleIds || [],
      moderatorRoleIds: c.moderatorRoleIds || [],
      userRoleIds: c.userRoleIds || [],
    };
  });

  return {
    enabled: Boolean(raw.enabled),
    botToken: raw.botToken || '',
    applicationId: raw.applicationId || '',
    categories,
    categoryChannels,
    messageRouting,
    adminChannelId: raw.adminChannelId || '',
    statisticsChannelId: raw.statisticsChannelId || '',
    adminRoleIds: raw.adminRoleIds || [],
    moderatorRoleIds: raw.moderatorRoleIds || [],
    userRoleIds: raw.userRoleIds || [],
    championships,
  };
};

// ---- Sub-components ----

const RoleIdList: React.FC<{
  label: string;
  ids: string[];
  onChange: (ids: string[]) => void;
}> = ({ label, ids, onChange }) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (v && !ids.includes(v)) onChange([...ids, v]);
    setDraft('');
  };
  return (
    <div className="role-list">
      <label className="field-label">{label}</label>
      <div className="role-tags">
        {ids.map((id) => (
          <span key={id} className="role-tag">
            {id}
            <button className="role-tag-remove" onClick={() => onChange(ids.filter((x) => x !== id))}>×</button>
          </span>
        ))}
      </div>
      <div className="role-input-row">
        <input
          className="discord-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="Role ID"
        />
        <button className="discord-btn-small" onClick={add}>Add</button>
      </div>
    </div>
  );
};

const ChannelField: React.FC<{
  label: string;
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
}> = ({ label, value, placeholder, onChange }) => (
  <div className="discord-field">
    <label className="field-label">{label}</label>
    <input
      className="discord-input"
      value={value}
      placeholder={placeholder || 'Channel ID'}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

// ---- Main component ----

const DiscordBot: React.FC = () => {
  const [config, setConfig] = useState<DiscordBotConfigData>(EMPTY_CONFIG);
  const [botStatus, setBotStatus] = useState<'online' | 'offline'>('offline');
  const [saving, setSaving] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [expandedChampionships, setExpandedChampionships] = useState<Set<string>>(new Set());
  const [championships, setChampionships] = useState<Championship[]>([]);
  const [newCategoryName, setNewCategoryName] = useState('');

  useEffect(() => {
    loadConfig();
    loadChampionships();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await api.getDiscordBotConfig();
      const raw = res.data?.config || {};
      setConfig(normaliseConfig(raw));
      setBotStatus(res.data?.status || 'offline');
    } catch {
      // no config yet, use defaults
    }
  };

  const loadChampionships = async () => {
    try {
      const res = await api.getChampionships();
      setChampionships(res.data?.championships || []);
    } catch { /* ignore */ }
  };

  const save = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      const res = await api.saveDiscordBotConfig(config);
      setBotStatus(res.data?.status || 'offline');
      setSaveMsg('Saved.');
    } catch (err: any) {
      setSaveMsg(`Error: ${err.response?.data?.error || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const restart = async () => {
    setRestarting(true);
    setSaveMsg('');
    try {
      const res = await api.restartDiscordBot();
      setBotStatus(res.data?.status || 'offline');
      setSaveMsg('Bot restarted.');
    } catch (err: any) {
      setSaveMsg(`Error: ${err.response?.data?.error || err.message}`);
    } finally {
      setRestarting(false);
    }
  };

  const setField = (field: keyof DiscordBotConfigData, value: any) =>
    setConfig((prev) => ({ ...prev, [field]: value }));

  // Category management
  const addCategory = () => {
    const name = newCategoryName.trim();
    if (!name) return;
    const key = name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    if (!key || config.categories.some((c) => c.key === key)) return;
    setConfig((prev) => ({ ...prev, categories: [...prev.categories, { key, name }] }));
    setNewCategoryName('');
  };

  const removeCategory = (key: string) => {
    setConfig((prev) => {
      const cats = prev.categories.filter((c) => c.key !== key);
      const catChannels = { ...prev.categoryChannels };
      delete catChannels[key];
      // Reroute messages that pointed to deleted key → first remaining category or ''
      const fallback = cats[0]?.key || '';
      const routing = Object.fromEntries(
        Object.entries(prev.messageRouting).map(([t, k]) => [t, k === key ? fallback : k])
      );
      return { ...prev, categories: cats, categoryChannels: catChannels, messageRouting: routing };
    });
  };

  const renameCategory = (key: string, name: string) => {
    setConfig((prev) => ({
      ...prev,
      categories: prev.categories.map((c) => c.key === key ? { ...c, name } : c),
    }));
  };

  const setCategoryChannel = (key: string, channelId: string) => {
    setConfig((prev) => ({
      ...prev,
      categoryChannels: { ...prev.categoryChannels, [key]: channelId },
    }));
  };

  const setMessageRouting = (eventType: string, categoryKey: string) => {
    setConfig((prev) => ({
      ...prev,
      messageRouting: { ...prev.messageRouting, [eventType]: categoryKey },
    }));
  };

  // Championship helpers
  const getChampCfg = (champId: string): ChampionshipDiscordConfig => {
    return config.championships.find((c) => c.championshipId === champId) || EMPTY_CHAMP_CFG(champId);
  };

  const updateChampCfg = (champId: string, patch: Partial<ChampionshipDiscordConfig>) => {
    setConfig((prev) => {
      const existing = prev.championships.find((c) => c.championshipId === champId);
      if (existing) {
        return {
          ...prev,
          championships: prev.championships.map((c) =>
            c.championshipId === champId ? { ...c, ...patch } : c
          ),
        };
      }
      return {
        ...prev,
        championships: [...prev.championships, { ...EMPTY_CHAMP_CFG(champId), ...patch }],
      };
    });
  };

  const toggleChampionship = (champId: string) => {
    setExpandedChampionships((prev) => {
      const next = new Set(prev);
      if (next.has(champId)) next.delete(champId);
      else next.add(champId);
      return next;
    });
  };

  const discordBotUrl = config.applicationId
    ? `https://discord.com/api/oauth2/authorize?client_id=${config.applicationId}&permissions=248832&scope=bot%20applications.commands`
    : null;

  return (
    <div className="discord-bot-page">

      {/* ---- Bot credentials ---- */}
      <div className="discord-section">
        <h2>Discord Bot</h2>
        <p className="discord-description">
          Configure the Discord bot to post automated messages to your server channels
          and allow drivers to use slash commands from Discord.
        </p>

        <div className="discord-status-row">
          <span className={`bot-status-badge ${botStatus}`}>{botStatus === 'online' ? 'Online' : 'Offline'}</span>
          {discordBotUrl ? (
            <a href={discordBotUrl} target="_blank" rel="noreferrer" className="discord-btn-secondary">
              Add Bot to Discord Server
            </a>
          ) : (
            <span className="discord-btn-secondary discord-btn-disabled" title="Enter Application ID first">
              Add Bot to Discord Server
            </span>
          )}
          <button className="discord-btn-small" onClick={restart} disabled={restarting}>
            {restarting ? 'Restarting...' : 'Restart Bot'}
          </button>
        </div>

        <div className="discord-field discord-toggle-row">
          <label className="field-label">Discord Bot Enabled</label>
          <label className="toggle">
            <input type="checkbox" checked={config.enabled} onChange={(e) => setField('enabled', e.target.checked)} />
            <span className="toggle-slider" />
          </label>
        </div>

        <div className="discord-field">
          <label className="field-label">Application ID</label>
          <input
            className="discord-input"
            value={config.applicationId}
            placeholder="Application ID from General Information in the Developer Portal"
            onChange={(e) => setField('applicationId', e.target.value)}
          />
          <p className="discord-hint">Found under General Information in your Discord application. Used to build the invite link above.</p>
        </div>

        <div className="discord-field">
          <label className="field-label">Bot Token</label>
          <input
            className="discord-input"
            type="password"
            value={config.botToken}
            placeholder={config.botToken === '***' ? '(saved — enter new value to change)' : 'Bot token from the Bot tab in the Developer Portal'}
            onChange={(e) => setField('botToken', e.target.value)}
          />
          <p className="discord-hint">Found under the Bot tab in your Discord application. Enable all Privileged Gateway Intents on that same page.</p>
        </div>
      </div>

      {/* ---- Channel Categories ---- */}
      <div className="discord-section">
        <h3>Channel Categories</h3>
        <p className="discord-hint">
          Define named categories. Each category maps to one Discord channel.
          Messages are then routed to a category in the section below.
        </p>

        <div className="category-list">
          {config.categories.map((cat) => (
            <div key={cat.key} className="category-row">
              <input
                className="discord-input category-name-input"
                value={cat.name}
                onChange={(e) => renameCategory(cat.key, e.target.value)}
                placeholder="Category name"
              />
              <span className="category-key-hint">key: {cat.key}</span>
              <button
                className="discord-btn-small category-remove-btn"
                onClick={() => removeCategory(cat.key)}
                disabled={config.categories.length <= 1}
                title={config.categories.length <= 1 ? 'Cannot remove the last category' : 'Remove category'}
              >
                Remove
              </button>
            </div>
          ))}
        </div>

        <div className="category-add-row">
          <input
            className="discord-input"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCategory(); } }}
            placeholder="New category name (e.g. Announcements)"
          />
          <button className="discord-btn-small" onClick={addCategory}>Add Category</button>
        </div>
      </div>

      {/* ---- Message Routing ---- */}
      <div className="discord-section">
        <h3>Message Routing</h3>
        <p className="discord-hint">Assign each type of automatic Discord message to a channel category.</p>

        <div className="routing-table">
          {MESSAGE_TYPES.map(({ type, label }) => (
            <div key={type} className="routing-row">
              <span className="routing-label">{label}</span>
              <select
                className="discord-input routing-select"
                value={config.messageRouting[type] || config.categories[0]?.key || ''}
                onChange={(e) => setMessageRouting(type, e.target.value)}
              >
                {config.categories.map((cat) => (
                  <option key={cat.key} value={cat.key}>{cat.name}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
      </div>

      {/* ---- Channel Assignments ---- */}
      <div className="discord-section">
        <h3>Channel Assignments</h3>
        <p className="discord-hint">Enter the Discord channel ID for each category and fixed system channel.</p>

        {config.categories.map((cat) => (
          <ChannelField
            key={cat.key}
            label={`${cat.name} Channel ID`}
            value={config.categoryChannels[cat.key] || ''}
            onChange={(v) => setCategoryChannel(cat.key, v)}
          />
        ))}

        <div className="channel-fixed-divider">
          <span>Fixed system channels</span>
        </div>

        <ChannelField
          label="Admin Channel ID (for admin command feedback)"
          value={config.adminChannelId}
          onChange={(v) => setField('adminChannelId', v)}
        />
        <ChannelField
          label="Statistics Channel ID (live server & standings overview)"
          value={config.statisticsChannelId}
          onChange={(v) => setField('statisticsChannelId', v)}
        />
      </div>

      {/* ---- Role Settings ---- */}
      <div className="discord-section">
        <h3>Role Settings</h3>
        <p className="discord-hint">Enter Discord role IDs. Right-click a role in Discord with Developer Mode on to copy its ID.</p>
        <RoleIdList label="Admin Role IDs" ids={config.adminRoleIds} onChange={(v) => setField('adminRoleIds', v)} />
        <RoleIdList label="Moderator Role IDs" ids={config.moderatorRoleIds} onChange={(v) => setField('moderatorRoleIds', v)} />
        <RoleIdList label="User Role IDs (pinged in announcements)" ids={config.userRoleIds} onChange={(v) => setField('userRoleIds', v)} />
      </div>

      {/* ---- Per-Championship Overrides ---- */}
      {championships.length > 0 && (
        <div className="discord-section">
          <h3>Per-Championship Overrides</h3>
          <p className="discord-hint">
            Enable a championship to override channel and role settings for it specifically.
            Admin and moderator role IDs are merged with global; user role IDs override the global list.
          </p>
          {championships.map((champ) => {
            const cfg = getChampCfg(champ.id);
            const expanded = expandedChampionships.has(champ.id);
            return (
              <div key={champ.id} className="champ-discord-block">
                <div className="champ-discord-header" onClick={() => toggleChampionship(champ.id)}>
                  <span className="champ-discord-name">{champ.name}</span>
                  <label className="toggle" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={cfg.enabled}
                      onChange={(e) => {
                        updateChampCfg(champ.id, { enabled: e.target.checked });
                        if (e.target.checked && !expanded) toggleChampionship(champ.id);
                      }}
                    />
                    <span className="toggle-slider" />
                  </label>
                  <span className="champ-discord-chevron">{expanded ? '▲' : '▼'}</span>
                </div>

                {expanded && (
                  <div className="champ-discord-body">
                    <p className="discord-hint" style={{ marginBottom: 12 }}>
                      Leave a channel ID empty to inherit the global assignment.
                    </p>

                    {config.categories.map((cat) => (
                      <ChannelField
                        key={cat.key}
                        label={`${cat.name} Channel ID`}
                        value={cfg.categoryChannels[cat.key] || ''}
                        placeholder="Inherits global if empty"
                        onChange={(v) => {
                          const updated = { ...cfg.categoryChannels, [cat.key]: v };
                          updateChampCfg(champ.id, { categoryChannels: updated });
                        }}
                      />
                    ))}

                    <div className="channel-fixed-divider">
                      <span>Fixed system channel overrides</span>
                    </div>

                    <ChannelField
                      label="Admin Channel ID"
                      value={cfg.adminChannelId}
                      placeholder="Inherits global if empty"
                      onChange={(v) => updateChampCfg(champ.id, { adminChannelId: v })}
                    />
                    <ChannelField
                      label="Statistics Channel ID"
                      value={cfg.statisticsChannelId}
                      placeholder="Inherits global if empty"
                      onChange={(v) => updateChampCfg(champ.id, { statisticsChannelId: v })}
                    />

                    <RoleIdList
                      label="Admin Role IDs (merged with global)"
                      ids={cfg.adminRoleIds}
                      onChange={(v) => updateChampCfg(champ.id, { adminRoleIds: v })}
                    />
                    <RoleIdList
                      label="Moderator Role IDs (merged with global)"
                      ids={cfg.moderatorRoleIds}
                      onChange={(v) => updateChampCfg(champ.id, { moderatorRoleIds: v })}
                    />
                    <RoleIdList
                      label="User Role IDs (overrides global)"
                      ids={cfg.userRoleIds}
                      onChange={(v) => updateChampCfg(champ.id, { userRoleIds: v })}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ---- Save ---- */}
      <div className="discord-section discord-actions">
        <button className="discord-btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
        {saveMsg && <span className="discord-save-msg">{saveMsg}</span>}
      </div>

      {/* ---- Help ---- */}
      <div className="discord-section discord-help">
        <h3>Setup</h3>
        <ol>
          <li>Go to discord.com/developers/applications and create a new application.</li>
          <li>Under the Bot tab, add a bot user and copy the token.</li>
          <li>Enable all Privileged Gateway Intents (Server Members, Message Content, Presence).</li>
          <li>Click "Add Bot to Discord Server" above and authorise it in your server.</li>
          <li>Add your categories, route messages to them, enter the channel IDs, enable the bot, and save.</li>
          <li>Right-click channels and roles in Discord with Developer Mode on to copy their IDs.</li>
        </ol>

        <h3>Slash Commands</h3>
        <p>Admin commands (requires Admin Role):</p>
        <ul>
          <li>/server [START|STOP|RESTART|STATUS] [server name]</li>
          <li>/get-result [championship] [round]</li>
          <li>/sync [server name]</li>
          <li>/rotate [server name]</li>
        </ul>
        <p>User commands:</p>
        <ul>
          <li>/signout [championship] [race] — Sign out of an upcoming race</li>
          <li>/signin [championship] [race] — Sign back in to an upcoming race</li>
        </ul>
      </div>
    </div>
  );
};

export default DiscordBot;
